import { useLayoutEffect, useState } from 'react';
import { Button, Gray600, ICON_COLOR, IconButton } from '@zep-us/zus';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { useJoinWithLocale, useViewport } from '@/hooks';
import { ChartLineIcon, CodeQrIcon, EnterIcon, PenPaperIcon, SquareStackIcon } from '@/icons';
import { FileDownloadFillIcon } from '@/icons/components/FileDownloadFill';
const useShowOnlyIconViewport = () => {
  const [isShowOnlyIcon, setIsShowOnlyIcon] = useState(false);
  const {
    isMobileSm,
    isMobileLg,
    isTabletSm
  } = useViewport();
  useLayoutEffect(() => {
    if (isMobileSm || isMobileLg || isTabletSm) {
      setIsShowOnlyIcon(true);
    } else {
      setIsShowOnlyIcon(false);
    }
  }, [isMobileSm, isMobileLg, isTabletSm]);
  return isShowOnlyIcon;
};
export const EditButton = ({
  onClick,
  className,
  variant = 'contained',
  intent = 'alternative'
}: {
  onClick?: () => void;
  className?: string;
  variant?: 'contained' | 'outlined';
  intent?: 'alternative' | 'neutral';
}) => {
  const {
    t
  } = useTranslation(['common']);
  const isShowOnlyIcon = useShowOnlyIconViewport();
  if (isShowOnlyIcon) {
    return <IconButton size="lg" variant={variant} intent={intent} className={className} onClick={onClick}>
        <PenPaperIcon size="lg" />
      </IconButton>;
  }
  return <Button size="lg" variant={variant} intent={intent} className={className} onClick={onClick} leftSlot={<PenPaperIcon size="md" />} data-sentry-element="Button" data-sentry-component="EditButton" data-sentry-source-file="Buttons.tsx">
      {t('common:global.edit.button')}
    </Button>;
};
export const EnterButton = ({
  onClick,
  disabled = false,
  className
}: {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <Button size="lg" className={className} onClick={onClick} disabled={disabled} data-sentry-element="Button" data-sentry-component="EnterButton" data-sentry-source-file="Buttons.tsx">
      <EnterIcon size="md" fill={'#FFF'} data-sentry-element="EnterIcon" data-sentry-source-file="Buttons.tsx" />
      <div>{t('common:global.enter.button')}</div>
    </Button>;
};
export const DuplicateButton = ({
  onClick
}: {
  onClick?: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const isShowOnlyIcon = useShowOnlyIconViewport();
  if (isShowOnlyIcon) {
    return <IconButton size="lg" intent="neutral" onClick={onClick}>
        <SquareStackIcon size="lg" fill={Gray600} />
      </IconButton>;
  }
  return <Button size="lg" intent={'neutral'} onClick={onClick} data-sentry-element="Button" data-sentry-component="DuplicateButton" data-sentry-source-file="Buttons.tsx">
      <SquareStackIcon size="md" fill={Gray600} data-sentry-element="SquareStackIcon" data-sentry-source-file="Buttons.tsx" />
      {t('global.duplicate.button')}
    </Button>;
};
export const ShareButton = ({
  onClick,
  disabled = false
}: {
  onClick?: () => void;
  disabled?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const isShowOnlyIcon = useShowOnlyIconViewport();
  if (isShowOnlyIcon) {
    return <IconButton size="lg" className={'bg-[#15B09C] hover:bg-[#15B09C] disabled:bg-[#A8F6E8]'} onClick={onClick}>
        <CodeQrIcon size="lg" />
      </IconButton>;
  }
  return <Button size="lg" className={'bg-[#15B09C] hover:bg-[#15B09C] disabled:bg-[#A8F6E8]'} onClick={onClick} disabled={disabled} data-sentry-element="Button" data-sentry-component="ShareButton" data-sentry-source-file="Buttons.tsx">
      <CodeQrIcon size="md" data-sentry-element="CodeQrIcon" data-sentry-source-file="Buttons.tsx" />
      {t('global.share.button')}
    </Button>;
};
export const GoToLmsButton = ({
  quizId,
  onClick,
  className
}: {
  quizId: string;
  onClick?: () => void;
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const joinWithLocale = useJoinWithLocale();
  return <Button size="lg" className={className} intent={'alternative'} asChild onClick={onClick} data-sentry-element="Button" data-sentry-component="GoToLmsButton" data-sentry-source-file="Buttons.tsx">
      <Link className="relative" href={joinWithLocale(`/lms/${quizId}/learning-report`)} target="_blank" prefetch={false} data-sentry-element="Link" data-sentry-source-file="Buttons.tsx">
        <ChartLineIcon className="[&>*]:fill-strong" size="md" data-sentry-element="ChartLineIcon" data-sentry-source-file="Buttons.tsx" />
        {t('lmsBtn')}
      </Link>
    </Button>;
};
export const DuplicateAndEditButton = ({
  onClick
}: {
  onClick?: () => void;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <Button size="lg" intent="strong" className={'w-full tablet-sm:w-fit'} variant={'contained'} leftSlot={<SquareStackIcon size="md" />} onClick={onClick} data-sentry-element="Button" data-sentry-component="DuplicateAndEditButton" data-sentry-source-file="Buttons.tsx">
      {t('common:quizDetail.copyAndEdit')}
    </Button>;
};
export const QuizWorksheetExportButton = ({
  className,
  onClick,
  rightSlot,
  useTooltip
}: {
  className?: string;
  onClick: () => void;
  rightSlot?: JSX.Element;
  useTooltip: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const Comp = <Button size="md" className={className} variant="outlined" intent="alternative" onClick={onClick} rightSlot={rightSlot} leftSlot={<FileDownloadFillIcon size="sm" />}>
      {t('common:quizWorksheet.label')}
    </Button>;
  if (!useTooltip) {
    return Comp;
  }
  return <Tooltip className={'bg-white text-default'} side="top" arrow={{
    fill: ICON_COLOR.inverse
  }} trigger={Comp} content={t('common:quizWorksheet.description')} data-sentry-element="Tooltip" data-sentry-component="QuizWorksheetExportButton" data-sentry-source-file="Buttons.tsx"></Tooltip>;
};